// Recipe + recipe-attachment + bulk-import client helpers extracted from
// `src/lib/manufacturingClient.ts` to keep that entrypoint under the
// file-size hygiene threshold. The barrel re-exports everything here so
// existing imports keep working.

import { manufacturingFetch } from "@/lib/manufacturingClient";
import type {
  MfgRecipeAttachmentsListResponse,
  MfgRecipeAttachmentsSetResponse,
  MfgRecipeBulkCreateRequest,
  MfgRecipeBulkCreateResponse,
  MfgRecipeConfirmReviewRequest,
  MfgRecipeConfirmReviewResponse,
  MfgRecipeCreateResponse,
  MfgRecipeDetailResponse,
  MfgRecipeDeleteResponse,
  MfgRecipeKind,
  MfgRecipeLineRole,
  MfgRecipeLinesSetResponse,
  MfgRecipePendingLineCreateRequest,
  MfgRecipePendingLineCreateResponse,
  MfgRecipePendingLineReconcileRequest,
  MfgRecipePendingLineReconcileResponse,
  MfgRecipePendingLinesAllResponse,
  MfgRecipePendingLinesCountsResponse,
  MfgRecipePendingLinesListResponse,
  MfgRecipeUpdateResponse,
  MfgRecipesBulkDeleteResponse,
  MfgRecipesBulkUpdateMetaResponse,
  MfgRecipesListResponse,
} from "@/lib/manufacturing/types";

export function listManufacturingRecipes() {
  return manufacturingFetch<MfgRecipesListResponse>("/api/mfg/recipes", { method: "GET" });
}

export function getManufacturingRecipeDetail(recipeId: string, opts?: { explode?: boolean }) {
  const q = new URLSearchParams({ recipe_id: recipeId });
  if (opts?.explode) q.set("explode", "1");
  return manufacturingFetch<MfgRecipeDetailResponse>(
    `/api/mfg/recipes/detail?${q.toString()}`,
    { method: "GET" }
  );
}

/** Bulk recipe+BOM load for CSV/PDF export (no nested cost rollup). */
export function exportManufacturingRecipesBundle(recipeIds: readonly string[]) {
  return manufacturingFetch<{
    ok: true;
    organization_id: string;
    recipes: Array<
      Omit<
        MfgRecipeDetailResponse,
        "ok" | "organization_id" | "rollup_warning" | "line_extended" | "leaf_breakdown"
      > & {
        estimated_materials_cost: number;
        rollup_mode: "flat_materials";
      }
    >;
  }>("/api/mfg/recipes/export", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ recipe_ids: [...recipeIds] }),
  });
}

export function createManufacturingRecipe(payload: {
  code: string;
  name: string;
  notes?: string;
  kind?: MfgRecipeKind;
  tags?: string[];
  recipe_group_id?: string | null;
  needs_review?: boolean;
}) {
  return manufacturingFetch<MfgRecipeCreateResponse>("/api/mfg/recipes/create", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function updateManufacturingRecipe(payload: {
  recipe_id: string;
  name?: string;
  notes?: string;
  is_active?: boolean;
  kind?: MfgRecipeKind;
  tags?: string[];
  code?: string;
  recipe_group_id?: string | null;
  default_named_variant_application_ids?: string[];
  current_wholesale_usd?: number | null;
  current_msrp_usd?: number | null;
}) {
  return manufacturingFetch<MfgRecipeUpdateResponse>("/api/mfg/recipes/update", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function bulkUpdateManufacturingRecipesMeta(payload: {
  recipe_ids: string[];
  kind?: MfgRecipeKind;
  is_active?: boolean;
  recipe_group_id?: string | null;
  tags?: { mode: "add" | "remove" | "replace"; tags: string[] };
  rename?: {
    field: "name" | "code" | "both";
    find: string;
    replace: string;
    case_insensitive?: boolean;
  };
}) {
  return manufacturingFetch<MfgRecipesBulkUpdateMetaResponse>(
    "/api/mfg/recipes/bulk-update-meta",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export function deleteManufacturingRecipe(payload: { recipe_id: string }) {
  return manufacturingFetch<MfgRecipeDeleteResponse>("/api/mfg/recipes/delete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function bulkDeleteManufacturingRecipes(payload: { recipe_ids: string[] }) {
  return manufacturingFetch<MfgRecipesBulkDeleteResponse>("/api/mfg/recipes/bulk-delete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function setManufacturingRecipeLines(payload: {
  recipe_id: string;
  lines: Array<{
    material_id?: string;
    component_recipe_id?: string;
    material_use_id?: string;
    quantity: number;
    sort_order?: number;
    role?: MfgRecipeLineRole;
    swappable?: boolean;
    upgrade_price_delta?: number | null;
    slot_label?: string;
  }>;
}) {
  return manufacturingFetch<MfgRecipeLinesSetResponse>("/api/mfg/recipes/lines", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export type MfgRecipeLinesMaterialReplaceResponse = {
  ok: true;
  recipes: Array<{
    recipe_id: string;
    recipe_code: string;
    recipe_name: string;
    line_count: number;
  }>;
  lines: number;
  updated_count: number;
  source_deactivated: boolean;
};

export function replaceManufacturingRecipeLinesMaterial(payload: {
  from_material_id: string;
  to_material_id: string;
  qty_scale?: number;
  dry_run?: boolean;
  deactivate_source?: boolean;
}) {
  return manufacturingFetch<MfgRecipeLinesMaterialReplaceResponse>(
    "/api/mfg/recipes/lines/material-replace",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function bulkCreateManufacturingRecipes(payload: MfgRecipeBulkCreateRequest) {
  return manufacturingFetch<MfgRecipeBulkCreateResponse>(
    "/api/mfg/recipes/bulk-create",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function confirmManufacturingRecipesReview(payload: MfgRecipeConfirmReviewRequest) {
  return manufacturingFetch<MfgRecipeConfirmReviewResponse>(
    "/api/mfg/recipes/confirm-review",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function listManufacturingRecipePendingLines(recipeId: string) {
  const q = new URLSearchParams({ recipe_id: recipeId });
  return manufacturingFetch<MfgRecipePendingLinesListResponse>(
    `/api/mfg/recipes/pending-lines?${q.toString()}`,
    { method: "GET" }
  );
}

export function listManufacturingRecipePendingLineCounts() {
  return manufacturingFetch<MfgRecipePendingLinesCountsResponse>(
    "/api/mfg/recipes/pending-lines",
    { method: "GET" }
  );
}

export function listManufacturingAllPendingLines() {
  const q = new URLSearchParams({ scope: "all" });
  return manufacturingFetch<MfgRecipePendingLinesAllResponse>(
    `/api/mfg/recipes/pending-lines?${q.toString()}`,
    { method: "GET" }
  );
}

export function createManufacturingRecipePendingLine(
  payload: MfgRecipePendingLineCreateRequest
) {
  return manufacturingFetch<MfgRecipePendingLineCreateResponse>(
    "/api/mfg/recipes/pending-lines",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function reconcileManufacturingRecipePendingLine(
  payload: MfgRecipePendingLineReconcileRequest
) {
  return manufacturingFetch<MfgRecipePendingLineReconcileResponse>(
    "/api/mfg/recipes/pending-lines/reconcile",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function listManufacturingRecipeAttachments(parentRecipeId: string) {
  const q = new URLSearchParams({ recipe_id: parentRecipeId });
  return manufacturingFetch<MfgRecipeAttachmentsListResponse>(
    `/api/mfg/recipe-attachments?${q.toString()}`,
    { method: "GET" }
  );
}

export type MfgOrgSlotLabelsResponse = {
  ok: true;
  organization_id: string;
  labels: string[];
};

export function listManufacturingOrgSlotLabels() {
  return manufacturingFetch<MfgOrgSlotLabelsResponse>("/api/mfg/org-slot-labels", {
    method: "GET",
  });
}

export function setManufacturingRecipeAttachments(payload: {
  parent_recipe_id: string;
  attachments: Array<{
    attached_recipe_id: string;
    included_by_default?: boolean;
    customer_price_delta?: number | null;
    replaces_slot_labels?: string[];
    quantity?: number;
    sort_order?: number;
    notes?: string;
  }>;
}) {
  return manufacturingFetch<MfgRecipeAttachmentsSetResponse>(
    "/api/mfg/recipe-attachments/set",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}
